/**
 * DAMAGE SYSTEM - Sistema de Daños
 * Maneja la creación, reparación y tracking de daños en la casa
 */

import { DAMAGE_TYPES } from "../utils/constants.js";

export default class DamageSystem {
  constructor(scene) {
    this.scene = scene;
    this.damages = new Map(); // Map<damageId, damageData>
    this.nextId = 0;
  }

  /**
   * Crear un nuevo daño
   * @param {object} component - Componente afectado
   * @param {object} damageType - Tipo de daño (CRACK o VINE)
   * @param {object} position - {x, y} posición del daño
   * @param {string} subAreaId - ID de sub-área (si aplica)
   * @returns {number} ID del daño creado
   */
  createDamage(component, damageType, position, subAreaId = null) {
    const damageId = this.nextId++;

    const damage = {
      id: damageId,
      component: component,
      type: damageType,
      position: position,
      subAreaId: subAreaId,
      repaired: false,
      createdAt: Date.now(),
    };

    this.damages.set(damageId, damage);
    this.onDamageCreated(damage);

    return damageId;
  }

  /**
   * Reparar un daño
   * @param {number} damageId - ID del daño
   * @returns {object|null} Datos del daño reparado o null si no existe
   */
  repairDamage(damageId) {
    const damage = this.damages.get(damageId);

    if (!damage) {
      console.warn(`Daño ${damageId} no encontrado`);
      return null;
    }

    if (damage.repaired) {
      console.warn(`Daño ${damageId} ya fue reparado`);
      return null;
    }

    damage.repaired = true;
    damage.repairedAt = Date.now();

    this.onDamageRepaired(damage);

    return damage;
  }

  /**
   * Obtener un daño por ID
   * @param {number} damageId
   * @returns {object|null}
   */
  getDamage(damageId) {
    return this.damages.get(damageId) || null;
  }

  /**
   * Obtener todos los daños activos (no reparados)
   * @returns {Array}
   */
  getActiveDamages() {
    return Array.from(this.damages.values()).filter((d) => !d.repaired);
  }

  /**
   * Obtener daños de un componente específico
   * @param {string} componentId - ID del componente
   * @returns {Array}
   */
  getDamagesByComponent(componentId) {
    return Array.from(this.damages.values()).filter(
      (d) => d.component.id === componentId && !d.repaired
    );
  }

  /**
   * Obtener daños de una sub-área específica
   * @param {string} componentId - ID del componente
   * @param {string} subAreaId - ID de la sub-área
   * @returns {Array}
   */
  getDamagesBySubArea(componentId, subAreaId) {
    return Array.from(this.damages.values()).filter(
      (d) =>
        d.component.id === componentId &&
        d.subAreaId === subAreaId &&
        !d.repaired
    );
  }

  /**
   * Obtener cantidad de daños activos
   * @returns {number}
   */
  getActiveDamageCount() {
    return this.getActiveDamages().length;
  }

  /**
   * Obtener cantidad de grietas activas
   * @returns {number}
   */
  getCrackCount() {
    return this.getActiveDamages().filter(
      (d) => d.type.id === DAMAGE_TYPES.CRACK.id
    ).length;
  }

  /**
   * Obtener cantidad de vines activas
   * @returns {number}
   */
  getVineCount() {
    return this.getActiveDamages().filter(
      (d) => d.type.id === DAMAGE_TYPES.VINE.id
    ).length;
  }

  /**
   * Verificar si un componente tiene daños
   * @param {string} componentId
   * @returns {boolean}
   */
  componentHasDamage(componentId) {
    return this.getDamagesByComponent(componentId).length > 0;
  }

  /**
   * Verificar si un componente alcanzó su máximo de daños
   * @param {object} component
   * @returns {boolean}
   */
  componentAtMaxDamage(component) {
    if (!component) return false;
    const currentDamages = this.getDamagesByComponent(component.id).length;
    return currentDamages >= component.maxDamage;
  }

  /**
   * Limpiar daños reparados (garbage collection)
   */
  cleanupRepairedDamages() {
    const toDelete = [];

    for (const [id, damage] of this.damages.entries()) {
      if (damage.repaired) {
        toDelete.push(id);
      }
    }

    toDelete.forEach((id) => this.damages.delete(id));

    if (toDelete.length > 0) {
      console.log(`🧹 ${toDelete.length} daños reparados limpiados`);
    }
  }

  /**
   * Resetear todos los daños
   */
  reset() {
    this.damages.clear();
    this.nextId = 0;
    console.log("🔄 Sistema de daños reseteado");
  }

  /**
   * Obtener estadísticas de daños
   * @returns {object}
   */
  getStats() {
    const active = this.getActiveDamages();
    const repaired = Array.from(this.damages.values()).filter(
      (d) => d.repaired
    );

    return {
      total: this.damages.size,
      active: active.length,
      repaired: repaired.length,
      cracks: this.getCrackCount(),
      vines: this.getVineCount(),
    };
  }

  /**
   * Callback cuando se crea un daño
   */
  onDamageCreated(damage) {
    this.scene.events.emit("damageCreated", damage);
    if (damage && damage.type && damage.component) {
      console.log(
        `💥 Daño creado: ${damage.type.name} en ${damage.component.name}`
      );
    }
  }

  /**
   * Callback cuando se repara un daño
   */
  onDamageRepaired(damage) {
    this.scene.events.emit("damageRepaired", damage);
    if (damage && damage.type && damage.component) {
      console.log(
        `✅ Daño reparado: ${damage.type.name} en ${damage.component.name}`
      );
    }
  }

  /**
   * Destruir el sistema
   */
  destroy() {
    this.reset();
    this.scene = null;
  }
}
